/**
 * v3.legacyApplications — Phase 1.0 #2 (Bill 2026-05-01) bridge for the
 * legacy SharePoint list `ao_OnlineApplications` (JotForm intake).
 *
 * Backed by Functions/LegacyApplicationsFunction.cs. Three routes:
 *
 *   GET  /v3/legacy-applications?branchCode={code}&pilotOnly=true
 *   POST /v3/legacy-applications/{spItemId}/import
 *   POST /v3/legacy-applications/{spItemId}/archive
 *
 * The list + import calls are pilot-gated server-side by feature
 * `legacy-import`. A 403 with errorCode='PILOT_NOT_ENROLLED' means the
 * officer can't see/claim legacy items; the RAO landing panel uses that
 * signal to render its "talk to Bill to enroll" banner.
 *
 * Phase 2 customer-portal applications will land in their own client
 * surface; this file stays scoped to the legacy bridge.
 */

import type { RequestOptions } from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

// -----------------------------------------------------------------------------
// Wire shapes
// -----------------------------------------------------------------------------

export interface LegacyApplicationItem {
  /** SharePoint list item id (string-encoded number). */
  readonly spItemId: string;
  readonly applicantName: string;
  readonly entityType: string | null;
  readonly productRequested: string | null;
  readonly branchCode: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  /** Legacy intake source label (defaults to "JotForm" for v1/v2). */
  readonly source: string;
  readonly createdByUser: string | null;
  readonly status: string;
  readonly submittedAt: string | null;
}

export interface LegacyApplicationsListResponse {
  readonly success: boolean;
  readonly data?: { readonly items: ReadonlyArray<LegacyApplicationItem> };
  readonly error?: string;
  readonly errorCode?: string;
}

export interface LegacyApplicationImportResponse {
  readonly success: boolean;
  readonly data?: {
    readonly cartId: string;
    readonly sharePointItemId: string;
    readonly alreadyImported: boolean;
  };
  readonly error?: string;
  readonly errorCode?: string;
  readonly spItemId?: string;
}

export interface LegacyApplicationArchiveResponse {
  readonly success: boolean;
  readonly data?: {
    readonly sharePointItemId: string;
    readonly cartId: string;
    readonly cifId: string | null;
    readonly accountId: string | null;
    readonly archived: boolean;
  };
  readonly error?: string;
  readonly errorCode?: string;
}

// -----------------------------------------------------------------------------
// Client
// -----------------------------------------------------------------------------

export class LegacyApplicationsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * List unclaimed legacy applications.
   *
   * v0.5.1 — pilot-gate removed (Bill 2026-05-03). Pilot is enforced by
   * Teams app provisioning, not at this endpoint. The `pilotOnly` filter
   * is kept on the type signature for back-compat but ignored by the
   * server.
   */
  public list(
    filters: { branchCode?: string; pilotOnly?: boolean; top?: number } = {},
    opts?: RequestOptions,
  ): Promise<LegacyApplicationsListResponse> {
    const query: Record<string, string | number | boolean | undefined> = {};
    if (filters.branchCode) query.branchCode = filters.branchCode;
    if (filters.top) query.top = filters.top;

    return this.http.get<LegacyApplicationsListResponse>('/v3/legacy-applications', {
      ...opts,
      query,
    });
  }

  /**
   * Claim & import. Idempotent on (spItemId): re-calling returns the
   * existing cart with `alreadyImported=true`. Server pilot-gates this
   * call by feature `legacy-import`.
   */
  public import(
    spItemId: string,
    body: { officerCode: string; branchCode?: string },
    opts?: RequestOptions,
  ): Promise<LegacyApplicationImportResponse> {
    return this.http.post<LegacyApplicationImportResponse>(
      `/v3/legacy-applications/${encodeURIComponent(spItemId)}/import`,
      { ...opts, body },
    );
  }

  /**
   * Archive callback — fired by the cart commit fan-out. Idempotent
   * (best-effort SP write). FSR-facing UI normally never calls this.
   */
  public archive(
    spItemId: string,
    body: { cartId: string; cifId?: string; accountId?: string },
    opts?: RequestOptions,
  ): Promise<LegacyApplicationArchiveResponse> {
    return this.http.post<LegacyApplicationArchiveResponse>(
      `/v3/legacy-applications/${encodeURIComponent(spItemId)}/archive`,
      { ...opts, body },
    );
  }
}
